/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Button,
  Spinner,
} from 'react-bootstrap';
import Select, { components } from 'react-select';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../services/axios';
import Loading from '../../components/Loading';
import Breadcrumbs from '../../components/Breadcrumbs';
import { useNotification } from '../../components/Notification/NotificationContext';

// --------------------------------------------------------------
// Componente customizado para o react-select (sem alterações)
// --------------------------------------------------------------
function CustomValueContainer(props) {
  const { children, selectProps, ...rest } = props;
  const allOptions = selectProps.options;
  return (
    <components.ValueContainer {...rest} selectProps={selectProps}>
      {children}
      {allOptions.length > 0 && (
        <button
          type="button"
          onClick={() => selectProps.onChange(allOptions)}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '0 4px',
            color: '#495057',
          }}
          title="Selecionar todos"
        >
          »»
        </button>
      )}
    </components.ValueContainer>
  );
}
CustomValueContainer.propTypes = {
  children: PropTypes.node.isRequired,
  selectProps: PropTypes.shape({
    options: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        label: PropTypes.string.isRequired,
      })
    ).isRequired,
    onChange: PropTypes.func.isRequired,
  }).isRequired,
};

export default function ServiceDeskEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const notification = useNotification();

  // Estados dos campos básicos
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');

  // Estados para TODAS as opções disponíveis
  const [allDepartments, setAllDepartments] = useState([]);
  const [allPriorities, setAllPriorities] = useState([]);
  const [allStages, setAllStages] = useState([]);

  // Estados para os valores selecionados
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedPriorities, setSelectedPriorities] = useState([]);
  const [selectedStages, setSelectedStages] = useState([]);

  const [loading, setLoading] = useState(true);

  // ====================================================================================
  // ✅ INÍCIO DA MODIFICAÇÃO: Substitua o seu useEffect por este bloco.
  // ====================================================================================
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Busca todos os dados necessários em paralelo de forma resiliente
        const results = await Promise.allSettled([
          axios.get(`/servicedesks/${id}`),
          axios.get('/departaments'),
          axios.get('/priorities'),
          axios.get('/stages'),
        ]);

        const deskResult = results[0];
        const departmentsResult = results[1];
        const prioritiesResult = results[2];
        const stagesResult = results[3];

        // Processa o resultado principal da Mesa de Serviço. Se falhar, não podemos continuar.
        if (deskResult.status === 'rejected') {
          throw new Error(
            'Falha ao carregar os dados principais da Mesa de Serviço.'
          );
        }
        const desk = deskResult.value.data;

        // Preenche os campos de texto
        setName(desk.name || '');
        setDisplayName(desk.displayName || '');
        setDescription(desk.description || '');

        // Popula as listas de TODAS as opções disponíveis
        const allDeptOptions =
          departmentsResult.status === 'fulfilled'
            ? (departmentsResult.value.data || []).map((d) => ({
                value: d.id,
                label: d.name,
              }))
            : [];
        const allPrioOptions =
          prioritiesResult.status === 'fulfilled'
            ? (prioritiesResult.value.data || []).map((p) => ({
                value: p.id,
                label: p.name,
              }))
            : [];
        const allStageOptions =
          stagesResult.status === 'fulfilled'
            ? (stagesResult.value.data || []).map((s) => ({
                value: s.id,
                label: s.name,
              }))
            : [];

        setAllDepartments(allDeptOptions);
        setAllPriorities(allPrioOptions);
        setAllStages(allStageOptions);

        // Cria uma lista de IDs que já estão selecionados na mesa
        const selectedDeptIds = new Set(
          (desk.departments || []).map((d) => d.id)
        );
        const selectedPrioIds = new Set(
          (desk.priorities || []).map((p) => p.id)
        );
        const selectedStageIds = new Set((desk.stages || []).map((s) => s.id));

        // Filtra a lista completa de OPÇÕES para encontrar os objetos que devem ser pré-selecionados
        setSelectedDepartments(
          allDeptOptions.filter((opt) => selectedDeptIds.has(opt.value))
        );
        setSelectedPriorities(
          allPrioOptions.filter((opt) => selectedPrioIds.has(opt.value))
        );
        setSelectedStages(
          allStageOptions.filter((opt) => selectedStageIds.has(opt.value))
        );
      } catch (err) {
        console.error('Erro ao carregar dados:', err);
        notification(
          err.message || 'Falha ao carregar dados da Mesa de Serviço',
          'error'
        );
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, notification, navigate]);
  // ====================================================================================
  // ✅ FIM DA MODIFICAÇÃO
  // ====================================================================================

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Envia apenas os IDs dos itens selecionados
      await axios.put(`/servicedesks/${id}`, {
        name,
        displayName,
        description,
        departmentIds: selectedDepartments.map((x) => x.value),
        priorityIds: selectedPriorities.map((x) => x.value),
        stageIds: selectedStages.map((x) => x.value),
      });

      notification('Mesa de serviço atualizada com sucesso', 'success');
      navigate('/servicedesk');
    } catch (err) {
      console.error('Erro ao atualizar Mesa de Serviço:', err);
      notification('Falha ao atualizar Mesa de Serviço', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Container fluid className="px-lg-4 px-xl-15">
      <Breadcrumbs
        title="Editar Mesa de Serviço"
        pages={[{ name: 'Mesa de Serviço', link: '/servicedesk' }]}
      />
      <Card className="mb-5">
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col>
                <Form.Group>
                  <Form.Label>Nome</Form.Label>
                  <Form.Control
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Form.Label>Nome de Exibição</Form.Label>
                  <Form.Control
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group>
                  <Form.Label>Descrição</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group>
                  <Form.Label>Departamentos</Form.Label>
                  <Select
                    isMulti
                    components={{ ValueContainer: CustomValueContainer }}
                    value={selectedDepartments}
                    onChange={setSelectedDepartments}
                    options={allDepartments}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Form.Label>Prioridades</Form.Label>
                  <Select
                    isMulti
                    components={{ ValueContainer: CustomValueContainer }}
                    value={selectedPriorities}
                    onChange={setSelectedPriorities}
                    options={allPriorities}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Form.Label>Estágios</Form.Label>
                  <Select
                    isMulti
                    components={{ ValueContainer: CustomValueContainer }}
                    value={selectedStages}
                    onChange={setSelectedStages}
                    options={allStages}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="justify-content-end">
              <Col xs="auto">
                <Button type="submit" variant="primary" disabled={loading}>
                  {loading ? (
                    <Spinner size="sm" animation="border" />
                  ) : (
                    'Atualizar Mesa De Serviço'
                  )}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
}
